// @flow
// ----------------------------------------------------------------------
// <copyright file="Izenda.ImportHistoryList.js" company="Izenda">
//  Copyright (c) 2015 Izenda, Inc.
//  ALL RIGHTS RESERVED
//
//  The entire contents of this file is protected by U.S. and
//  International Copyright Laws. Unauthorized reproduction,
//  reverse-engineering, and distribution of all or any portion of
//  the code contained in this file is strictly prohibited and may
//  result in severe civil and criminal penalties and will be
//  prosecuted to the maximum extent possible under the law.
//
//  RESTRICTIONS
//
//  THIS SOURCE CODE AND ALL RESULTING INTERMEDIATE FILES
//  ARE CONFIDENTIAL AND PROPRIETARY TRADE
//  SECRETS OF IZENDA INC. THE REGISTERED DEVELOPER IS
//  LICENSED TO DISTRIBUTE THE PRODUCT AND ALL ACCOMPANYING .NET
//  CONTROLS AS PART OF AN EXECUTABLE PROGRAM ONLY.
//
//  THE SOURCE CODE CONTAINED WITHIN THIS FILE AND ALL RELATED
//  FILES OR ANY PORTION OF ITS CONTENTS SHALL AT NO TIME BE
//  COPIED, TRANSFERRED, SOLD, DISTRIBUTED, OR OTHERWISE MADE
//  AVAILABLE TO OTHER INDIVIDUALS WITHOUT EXPRESS WRITTEN CONSENT
//  AND PERMISSION FROM IZENDA INC.
//
//  CONSULT THE END USER LICENSE AGREEMENT(EULA FOR INFORMATION ON
//  ADDITIONAL RESTRICTIONS.
//  </copyright>
// ----------------------------------------------------------------------

import React, { Component } from 'react';
import { Table, Column } from 'share/components/controls/DataGrid/Izenda.Table';
import PropTypes from 'prop-types';
import Input from 'share/components/controls/Izenda.Input';
import Paginator from 'share/components/controls/DataGrid/helpers/Izenda.Paginator';
import { TableDefinition, DEFAULT_REST_HEIGHT } from 'logic/data/Izenda.ImportHistoryConstants';
import { observable } from 'mobx';
import { map, merge, values } from 'lodash/fp';
import actions from './Izenda.ImportHistoryListActions';
import connect from 'share/enhancers/Izenda.Connect';
import uiState from 'logic/Izenda.UiState';
import Dimensions from 'share/components/hoc/Izenda.Dimensions';

const TABLE_DEF = TableDefinition.ImportHistorySourceType;

@connect(actions, Dimensions)
export default class ImportHistoryList extends Component {
  static propTypes = {
    store: PropTypes.object,
    loadHistories: PropTypes.func
  };

  /**
   * Set dom
   * @param {Object} dom
   */
  setDom = (dom: any): void => {
    this.domNode = dom;
  };

  componentDidMount() {
    const {
      store: { model }
    } = this.props;
    if (model.tableHeight != this.domNode.clientHeight) {
      model.setTableHeight(this.domNode.clientHeight);
    }
    model.resetPageInfo();
    this.props.loadHistories();
  }

  componentWillReceiveProps(nextProps) {
    const {
      store: { model }
    } = this.props;

    model.setTableHeight(nextProps.height);
  }

  /**
   * Render checkbox cell
   * @param {Object} dataItem
   */
  renderSelectCell = (dataItem: any) => {
    return (
      <input
        type="checkbox"
        checked={dataItem.isSelected}
        onChange={this.props.handleCheckItem.bind(this.props, dataItem)}
      />
    );
  };

  /**
   * Render label cell
   * @param {Object} dataItem
   * @param {String} columnKey
   */
  renderTextCell = (dataItem: any, columnKey: string) => {
    return (
      <span className="truncate overflow-text" title={dataItem[columnKey]}>
        {dataItem[columnKey]}
      </span>
    );
  };

  /**
   * Render date cell
   * @param {Object} dataItem
   */
  renderDateCell = (dataItem: any) => {
    return (
      <span className="truncate overflow-text" title={dataItem.dateLabel}>
        {dataItem.dateLabel}
      </span>
    );
  };

  /**
   * Render action cell
   * @param {Object} dataItem
   */
  renderActionCell = (dataItem: any) => {
    const { handleDeleteItem } = this.props;
    return (
      <div>
        <a className="action-column">
          <span className="icon-cancel" onClick={handleDeleteItem.bind(this.props, dataItem)} />
        </a>
      </div>
    );
  };

  /**
   * Render columns
   */
  renderColumns() {
    const {
      store: {
        model: { isAllChecked, isEmptyHistories }
      },
      handleCheckAll
    } = this.props;
    const checkBoxSelectAllProps = {
      onChange: handleCheckAll,
      type: 'checkbox',
      checked: isAllChecked,
      disabled: isEmptyHistories
    };

    const selectRowDefinition = merge(TABLE_DEF['select'], {
      headerComponent: Input,
      headerComponentProps: checkBoxSelectAllProps
    });

    return map(item => {
      switch (item.key) {
        case 'select':
          return <Column {...selectRowDefinition} align={'center'} cell={this.renderSelectCell} />;
        case 'fileName':
        case 'typeName':
        case 'modifiedBy':
        case 'statusName':
        case 'errorMessage':
        case 'objectName':
        case 'categoryName':
        case 'subCategoryName':
          return <Column {...item} cell={this.renderTextCell} />;
        case 'modified':
          return <Column {...item} cell={this.renderDateCell} />;
        case 'saveInto':
          return !uiState.tenantId && <Column {...item} cell={this.renderTextCell} />; // Only show in System mode
        case 'action':
          return <Column {...item} align={'center'} cell={this.renderActionCell} />;
        default:
          return <Column {...item} cell={this.renderTextCell} />;
      }
    }, values(TABLE_DEF));
  }

  render() {
    const {
      store: {
        model: { tableHeight, historyFiles, sortInfo, total, pageSize, pageIndex }
      }
    } = this.props;
    const newHeight =
      this.props.height > 0 ? this.props.height - DEFAULT_REST_HEIGHT : tableHeight - DEFAULT_REST_HEIGHT;

    return (
      <section className="izenda-Wrapper-content" ref={this.setDom}>
        <div className="u_fullHeight">
          <Table
            maxHeight={newHeight}
            minHeight={newHeight}
            dataSource={historyFiles}
            onSortChange={this.props.onSortChange}
            sortInfo={sortInfo}
            staticRender
          >
            {this.renderColumns()}
          </Table>
          <Paginator
            onChange={this.props.goToPage.bind(this.props)}
            onDropdownChange={this.props.onPageSizeChange.bind(this.props)}
            totalRecords={total}
            pageSize={pageSize}
            pageIndex={pageIndex}
          />
        </div>
      </section>
    );
  }
}



// WEBPACK FOOTER //
// ./ui/modules/Settings/DataSetup/ImportHistory/ImportHistoryList/Izenda.ImportHistoryList.js